import type { GlobalSettings } from '@services/wordpress/wordpress.types';
import type { States } from '@services/filter-api/types';

/**
 * Helper function to generate state-based navigation links
 */
export const getCareByStateSublinks = (states?: States) => 
  states?.map((state) => ({
    title: state.label,
    url: `/therapists/${state.slug}`,
  }));

/**
 * City-based navigation links
 */
export const getCareByCity = [
  {
    title: 'Los Angeles, CA',
    url: '/therapists/california/los-angeles',
  },
  {
    title: 'San Diego, CA',
    url: '/therapists/california/san-diego',
  },
  {
    title: 'Sacramento, CA',
    url: '/therapists/california/sacramento',
  },
  {
    title: 'Oakland, CA',
    url: '/therapists/california/oakland',
  },
  {
    title: 'San Francisco, CA',
    url: '/therapists/california/san-francisco',
  },
  {
    title: 'Houston, TX',
    url: '/therapists/texas/houston',
  },
  {
    title: 'San Antonio, TX',
    url: '/therapists/texas/san-antonio',
  },
  {
    title: 'Dallas, TX',
    url: '/therapists/texas/dallas',
  },
  {
    title: 'Austin, TX',
    url: '/therapists/texas/austin',
  },
  {
    title: 'Fort Worth, TX',
    url: '/therapists/texas/fort-worth',
  },
  {
    title: 'Miami, FL',
    url: '/therapists/florida/miami',
  },
  {
    title: 'Jacksonville, FL',
    url: '/therapists/florida/jacksonville',
  },
  {
    title: 'Orlando, FL',
    url: '/therapists/florida/orlando',
  },
  {
    title: 'Tampa, FL',
    url: '/therapists/florida/tampa',
  },
  {
    title: 'Chicago, IL',
    url: '/therapists/illinois/chicago',
  },
  {
    title: 'Philadelphia, PA',
    url: '/therapists/pennsylvania/philadelphia',
  },
  {
    title: 'New York, NY',
    url: '/therapists/new-york/new-york-city',
  },
  {
    title: 'Atlanta, GA',
    url: '/therapists/georgia/atlanta',
  },
  {
    title: 'Charlotte, NC',
    url: '/therapists/north-carolina/charlotte',
  },
  {
    title: 'Indianapolis, IN',
    url: '/therapists/indiana/indianapolis',
  },
];

export const headerLinks = (): GlobalSettings['headerLinks'] => [
  {
    linkLabel: "Get care",
    sublinks: [
      {
        sublink: {
          title: "How Grow works",
          url: "/how-it-works/",
        },
      },
    ],
  },
  {
    linkLabel: "Company",
    sublinks: [
      {
        sublink: {
          title: "About us",
          url: "/about-us/",
        },
      },
      {
        sublink: {
          title: "Blog",
          url: "/blog/",
        },
      },
      {
        sublink: {
          title: "Careers",
          url: "/careers/",
        },
      },
      {
        sublink: {
          title: "Partner with Grow",
          url: "/start/partner-with-grow/",
        },
      },
    ],
  },
  {
    linkLabel: "For providers",
    sublinks: [
      {
        sublink: {
          title: "Therapists",
          url: "/providers/",
        },
      },
      {
        sublink: {
          title: "Prescribers",
          url: "/providers-medication-management/",
        },
      },
      {
        sublink: {
          title: "Provider community",
          url: "/start/provider-community/",
        },
      },
      {
        sublink: {
          title: "Provider help center",
          url: "https://help.growtherapy.com/en/",
        },
      },
    ]
  },
] as GlobalSettings['headerLinks'];

export const expandedHeaderLinks = (states?: States): GlobalSettings['headerLinks'] => [
  {
    linkLabel: "How Grow works",
    linkUrl: "/how-it-works/",
  },
  {
    linkLabel: "Get care",
    sublinks: [
      {
        sublink: {
          title: "By state",
          sublinks: getCareByStateSublinks(states),
        },
      },
      {
        sublink: {
          title: "By city",
          sublinks: getCareByCity,
        },
      },
      {
        sublink: {
          title: "Therapy resources",
          sublinks: [
            {
              title: "Therapy 101",
              url: "/therapy-basics/types-of-therapy/",
            },
            {
              title: "What to expect",
              url: "/blog/first-therapy-session/",
            },
            {
              title: "Anxiety quiz",
              url: "/mental-health-tests/anxiety-test/",
            },
            {
              title: "ADHD quiz",
              url: "/mental-health-tests/adhd-test/",
            },
            {
              title: "Depression quiz",
              url: "/mental-health-tests/depression-test/",
            },
            {
              title: "Client help center",
              url: "https://help.growtherapy.com/clients/en/",
            }
          ]
        }
      }
    ],
  },
  {
    linkLabel: "Become a Grow provider",
    sublinks: [
      {
        sublink: {
          title: "Therapists / Counselors",
          url: "/providers/",
        },
      },
      {
        sublink: {
          title: "Prescribers",
          url: "/providers-medication-management/",
        },
      },
      {
        sublink: {
          title: "Provider resources",
          sublinks: [
            {
              title: "Provider community",
              url: "/start/provider-community/",
            },
            {
              title: "Provider help center",
              url: "https://help.growtherapy.com/en/",
            },
          ]
        },
      },
    ]
  },
  {
    linkLabel: "Partner with Grow",
    sublinks: [
      {
        sublink: {
          title: "Employers",
          url: "/start/employers/",
        },
      },
      {
        sublink: {
          title: "Physicians",
          url: "/start/pcp-physicians/",
        },
      },
      {
        sublink: {
          title: "Payors",
          url: "/payors/",
        },
      }
    ]
  },
  {
    linkLabel: "About us",
    linkUrl: "/about-us/",
  }
] as GlobalSettings['headerLinks'];
